import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import './StampCardsPage.css';

const PAGE_SIZE = 24;
const STAMPS_PER_CARD = 6;

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return val;
  }
}

function shortId(id) {
  if (!id) return '—';
  return `${String(id).slice(0, 8)}…`;
}

function StampCardVisual({ current = 0, perCard = STAMPS_PER_CARD }) {
  const filled = Math.max(0, Math.min(perCard, Number(current) || 0));
  return (
    <div
      className="stamp-card-visual"
      role="img"
      aria-label={`${filled} / ${perCard} damga`}
    >
      {Array.from({ length: perCard }).map((_, i) => (
        <span
          key={i}
          className={`stamp-dot${i < filled ? ' stamp-dot--filled' : ''}`}
        />
      ))}
    </div>
  );
}

function StampUserCard({ row, perCard, onGifted }) {
  const name = row.full_name || row.email || shortId(row.user_id);
  const current = Number(row.current_stamps) || 0;
  const completed = Number(row.completed_cards) || 0;
  const used = Number(row.used_stamps) || 0;
  const gifted = Number(row.gifted_stamps) || 0;
  const rewardReady = used > 0 && current === 0;
  const shown = rewardReady ? perCard : current;
  const progressPct = Math.round((shown / Math.max(1, perCard)) * 100);
  const initial = String(name).trim().charAt(0).toUpperCase() || '?';

  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const handleGift = async (e) => {
    e.preventDefault();
    const stamps = Math.max(1, Math.min(24, Number(qty) || 0));
    if (!window.confirm(`${name} kullanıcısına ${stamps} damga hediye edilsin mi?`)) {
      return;
    }
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const { data, error } = await supabase.rpc('admin_gift_stamps', {
        p_user_id: row.user_id,
        p_stamps: stamps,
        p_note: note.trim() || null,
      });
      if (error) throw error;
      let payload = data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { payload = {}; }
      }
      const added = Number(payload?.completed_cards_added) || 0;
      setMsg(
        added > 0
          ? `${stamps} damga hediye edildi · ${added} kart tamamlandı`
          : `${stamps} damga hediye edildi`,
      );
      setNote('');
      setQty(1);
      setOpen(false);
      onGifted?.(payload);
    } catch (ex) {
      setErr(ex?.message || 'Hediye başarısız.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="stamp-user-card">
      <header className="stamp-user-head">
        <span className="stamp-user-avatar" aria-hidden>{initial}</span>
        <div className="stamp-user-identity">
          <h3 className="stamp-user-name">{name}</h3>
          {row.full_name && row.email ? (
            <p className="stamp-user-email">{row.email}</p>
          ) : null}
          {row.phone ? <p className="stamp-user-phone">{row.phone}</p> : null}
        </div>
        <div className="stamp-user-badges">
          {rewardReady ? (
            <span className="stamp-badge stamp-badge--reward">Ödül turu</span>
          ) : null}
          <span className="stamp-badge">{completed} kart</span>
        </div>
      </header>

      <StampCardVisual current={shown} perCard={perCard} />

      <div className="stamp-progress" aria-hidden>
        <div className="stamp-progress-track">
          <span className="stamp-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="stamp-progress-label">{shown}/{perCard}</span>
      </div>

      <dl className="stamp-user-meta">
        <div>
          <dt>Toplam</dt>
          <dd>{used.toLocaleString('tr-TR')}</dd>
        </div>
        <div>
          <dt>Hediye</dt>
          <dd>{gifted.toLocaleString('tr-TR')}</dd>
        </div>
        <div>
          <dt>Son</dt>
          <dd title={fmtDate(row.last_used_at)}>{fmtDate(row.last_used_at)}</dd>
        </div>
      </dl>

      <div className="stamp-gift">
        {!open ? (
          <button
            type="button"
            className="stamp-gift-open"
            onClick={() => {
              setOpen(true);
              setErr('');
              setMsg('');
            }}
          >
            Damga hediye et
          </button>
        ) : (
          <form className="stamp-gift-form" onSubmit={handleGift}>
            <label className="stamp-gift-field">
              <span>Adet</span>
              <select
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                disabled={saving}
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </label>
            <label className="stamp-gift-field stamp-gift-field--grow">
              <span>Not</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Opsiyonel"
                maxLength={120}
                disabled={saving}
              />
            </label>
            <div className="stamp-gift-actions">
              <button type="submit" className="stamp-gift-submit" disabled={saving}>
                {saving ? '…' : 'Hediye et'}
              </button>
              <button
                type="button"
                className="stamp-gift-cancel"
                disabled={saving}
                onClick={() => setOpen(false)}
              >
                İptal
              </button>
            </div>
          </form>
        )}
        {msg ? <p className="stamp-gift-msg">{msg}</p> : null}
        {err ? <p className="stamp-gift-err" role="alert">{err}</p> : null}
      </div>
    </article>
  );
}

export default function StampCardsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [perCard, setPerCard] = useState(STAMPS_PER_CARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_stamp_cards', {
        p_limit: PAGE_SIZE,
        p_offset: page * PAGE_SIZE,
        p_search: searchDebounced || null,
      });
      if (rpcErr) throw rpcErr;

      let payload = data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { payload = {}; }
      }
      setTotal(Number(payload?.total) || 0);
      setPerCard(Number(payload?.stamps_per_card) || STAMPS_PER_CARD);
      setRows(Array.isArray(payload?.users) ? payload.users : []);
    } catch (e) {
      setError(e?.message || 'Damga kartları yüklenemedi.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGifted = useCallback(async (result) => {
    const gifted = Number(result?.gifted) || 0;
    setToast(gifted > 0 ? `${gifted} damga hediye edildi.` : 'Damga hediye edildi.');
    await load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const completedSum = rows.reduce((s, r) => s + (Number(r.completed_cards) || 0), 0);
  const activeSum = rows.reduce((s, r) => s + (Number(r.used_stamps) || 0), 0);

  return (
    <div className="admin-page-content stamp-page">
      {error ? (
        <p className="stamp-alert" role="alert">{error}</p>
      ) : null}
      {toast ? (
        <p className="stamp-toast" role="status">{toast}</p>
      ) : null}

      <div className="stamp-toolbar">
        <div className="stamp-search-wrap">
          <svg className="stamp-search-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="stamp-search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="E-posta, ad, telefon…"
            aria-label="Damga kartı ara"
          />
        </div>
        <div className="stamp-stats" aria-label="Özet">
          <div className="stamp-stat">
            <span className="stamp-stat-value">{total.toLocaleString('tr-TR')}</span>
            <span className="stamp-stat-label">Kullanıcı</span>
          </div>
          <div className="stamp-stat">
            <span className="stamp-stat-value">{activeSum.toLocaleString('tr-TR')}</span>
            <span className="stamp-stat-label">Damga</span>
          </div>
          <div className="stamp-stat">
            <span className="stamp-stat-value">{completedSum.toLocaleString('tr-TR')}</span>
            <span className="stamp-stat-label">Kart</span>
          </div>
        </div>
        <button type="button" className="stamp-refresh" onClick={load} disabled={loading}>
          {loading ? 'Yükleniyor…' : 'Yenile'}
        </button>
      </div>

      {loading ? (
        <div className="stamp-grid stamp-grid--loading" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="stamp-user-card stamp-user-card--shimmer" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="stamp-empty">Damga kaydı bulunamadı.</p>
      ) : (
        <div className="stamp-grid">
          {rows.map((row) => (
            <StampUserCard
              key={row.user_id}
              row={row}
              perCard={perCard}
              onGifted={handleGifted}
            />
          ))}
        </div>
      )}

      {totalPages > 1 ? (
        <nav className="stamp-pagination" aria-label="Sayfalama">
          <button
            type="button"
            className="stamp-page-btn"
            disabled={page <= 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Önceki
          </button>
          <span className="stamp-page-indicator">
            Sayfa {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="stamp-page-btn"
            disabled={page >= totalPages - 1 || loading}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          >
            Sonraki
          </button>
        </nav>
      ) : null}
    </div>
  );
}
